import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Briefcase, ChevronDown, Search, Check, ExternalLink, Kanban } from "lucide-react";
import { Opportunity, OpportunityStage } from "../../types";
import { subscribeToOpportunities } from "../../services/opportunitiesService";
import { useActiveOpportunity } from "../../hooks/useActiveOpportunity";

const ACTIVE_STAGES: OpportunityStage[] = ["reviewing", "go", "drafting", "in_review", "approved"];

const STAGE_LABELS: Record<OpportunityStage, string> = {
  reviewing: "Intake",
  no_go: "No-Go",
  go: "Qualified",
  drafting: "Drafting",
  in_review: "In Review",
  approved: "Approved",
  submitted: "Submitted",
  won: "Won",
  lost: "Lost",
  archived: "Archived",
};

const STAGE_BADGES: Record<OpportunityStage, string> = {
  reviewing:
    "bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border-amber-200 dark:border-amber-800",
  go: "bg-teal-50 text-[#2ba5b5] dark:bg-teal-950/60 dark:text-teal-300 border-teal-200 dark:border-teal-800",
  drafting:
    "bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border-blue-200 dark:border-blue-800",
  in_review:
    "bg-purple-50 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 border-purple-200 dark:border-purple-800",
  approved:
    "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800",
  submitted:
    "bg-cyan-50 text-cyan-700 dark:bg-cyan-950/60 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800",
  won: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200 border-emerald-300 dark:border-emerald-700",
  lost: "bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 border-rose-200 dark:border-rose-800",
  no_go:
    "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700",
  archived:
    "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 border-slate-200 dark:border-slate-700",
};

export const HeaderRfpSwitcher: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeOpp, setActiveOpp } = useActiveOpportunity();

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Subscribe to opportunities for switcher dropdown
  useEffect(() => {
    const unsubscribe = subscribeToOpportunities(
      (items) => setOpportunities(items),
      (err) => console.error("Failed to load opportunities in switcher:", err),
    );
    return () => unsubscribe();
  }, []);

  // Filter in-flight opportunities
  const inFlightOpportunities = useMemo(() => {
    return opportunities.filter((opp) => !opp.isArchived && ACTIVE_STAGES.includes(opp.stage));
  }, [opportunities]);

  const filteredOpportunities = useMemo(() => {
    if (!search.trim()) return inFlightOpportunities;
    const q = search.toLowerCase();
    return inFlightOpportunities.filter(
      (opp) => opp.title.toLowerCase().includes(q) || opp.client.toLowerCase().includes(q),
    );
  }, [inFlightOpportunities, search]);

  const closeDropdown = () => {
    setIsOpen(false);
    setSearch("");
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen]);

  const handleSelectOpportunity = (opp: Opportunity) => {
    setActiveOpp({
      id: opp.id,
      title: opp.title,
      client: opp.client,
      stage: opp.stage,
    });
    closeDropdown();
    navigate(`/opportunities/${opp.id}`);
  };

  const isWorkspaceRoute =
    location.pathname.startsWith("/opportunities/") || location.pathname === "/workspace";

  return (
    <div className="relative" ref={containerRef}>
      {/* Switcher Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label="Switch active RFP workspace"
        className={`group flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-all cursor-pointer ${
          isWorkspaceRoute && activeOpp
            ? "border-teal-200/80 bg-teal-50/70 text-slate-800 hover:border-[#2ba5b5] hover:bg-teal-50 dark:border-teal-800/60 dark:bg-teal-950/40 dark:text-slate-200 dark:hover:border-teal-700"
            : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700/80"
        }`}
      >
        <Briefcase className="h-3.5 w-3.5 text-[#2ba5b5] shrink-0" />

        {activeOpp ? (
          <div className="flex items-center gap-2 max-w-[280px] sm:max-w-[360px] truncate text-left">
            <span className="font-semibold text-slate-900 dark:text-white truncate">
              {activeOpp.client}:
            </span>
            <span className="text-slate-600 dark:text-slate-300 truncate">{activeOpp.title}</span>
            <span
              className={`hidden sm:inline-flex items-center px-1.5 py-0.2 rounded text-[10px] font-medium border ${
                STAGE_BADGES[activeOpp.stage] || STAGE_BADGES.reviewing
              }`}
            >
              {STAGE_LABELS[activeOpp.stage] || activeOpp.stage}
            </span>
          </div>
        ) : (
          <span className="text-slate-600 dark:text-slate-400">Workspace: Select Active RFP</span>
        )}

        <ChevronDown
          className={`h-3.5 w-3.5 text-slate-400 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Switcher Dropdown Menu */}
      {isOpen && (
        <div
          role="dialog"
          aria-label="Active RFP Switcher"
          className="glass absolute left-0 mt-1.5 w-80 sm:w-96 rounded-xl border border-slate-200 bg-white/95 p-2 shadow-xl backdrop-blur-md z-50 dark:border-slate-800 dark:bg-slate-900/95"
        >
          {/* Search Bar inside Dropdown */}
          <div className="relative mb-2 px-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search active proposals..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-md border border-slate-200 bg-slate-50/70 pl-8 pr-3 py-1.5 text-xs text-slate-900 placeholder-slate-400 focus:border-[#2ba5b5] focus:bg-white focus:outline-hidden dark:border-slate-800 dark:bg-slate-800/60 dark:text-slate-100 dark:placeholder-slate-500 dark:focus:border-[#2ba5b5] dark:focus:bg-slate-900"
            />
          </div>

          {/* List of Active Opportunities */}
          <div className="max-h-64 overflow-y-auto space-y-1 px-1">
            {filteredOpportunities.length === 0 ? (
              <div className="py-6 text-center text-xs text-slate-500 dark:text-slate-400">
                {search ? "No matching proposals" : "No active proposals found"}
              </div>
            ) : (
              filteredOpportunities.map((opp) => {
                const isCurrent = activeOpp?.id === opp.id;
                const badge = STAGE_BADGES[opp.stage] || STAGE_BADGES.reviewing;

                return (
                  <button
                    key={opp.id}
                    type="button"
                    onClick={() => handleSelectOpportunity(opp)}
                    className={`w-full group flex items-start justify-between gap-2 rounded-lg p-2 text-left text-xs transition-colors cursor-pointer ${
                      isCurrent
                        ? "bg-teal-50/80 text-teal-900 dark:bg-teal-950/50 dark:text-teal-200"
                        : "hover:bg-slate-100 dark:hover:bg-slate-800/80 text-slate-700 dark:text-slate-300"
                    }`}
                  >
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-slate-900 dark:text-white truncate">
                          {opp.client}
                        </span>
                        <span
                          className={`inline-flex items-center px-1.5 py-0.2 rounded text-[10px] font-medium border ${badge}`}
                        >
                          {STAGE_LABELS[opp.stage]}
                        </span>
                      </div>
                      <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">
                        {opp.title}
                      </p>
                    </div>

                    {isCurrent && <Check className="h-4 w-4 text-[#2ba5b5] shrink-0 mt-0.5" />}
                  </button>
                );
              })
            )}
          </div>

          {/* Bottom Actions */}
          <div className="mt-2 border-t border-slate-100 pt-2 px-1 flex items-center justify-between text-[11px] text-slate-500 dark:border-slate-800/80 dark:text-slate-400">
            <button
              type="button"
              onClick={() => {
                closeDropdown();
                navigate("/workspace");
              }}
              className="hover:text-[#2ba5b5] flex items-center gap-1 font-medium transition-colors cursor-pointer"
            >
              <ExternalLink className="h-3 w-3" />
              <span>Workspace Hub</span>
            </button>

            <button
              type="button"
              onClick={() => {
                closeDropdown();
                navigate("/");
              }}
              className="hover:text-[#2ba5b5] flex items-center gap-1 font-medium transition-colors cursor-pointer"
            >
              <Kanban className="h-3 w-3" />
              <span>Full Pipeline</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
